
import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

interface ExpenseReportListProps {
  organizationId: string;
  isManager: boolean;
  userId: string;
  onReportStatusChange?: () => void;
}

const ExpenseReportList = ({ 
  organizationId, 
  isManager, 
  userId,
  onReportStatusChange 
}: ExpenseReportListProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  console.log("🔍 DEBUG (ExpenseReportList): organizationId:", organizationId);
  console.log("🔍 DEBUG (ExpenseReportList): isMobile:", isMobile);

  const { data: reports, isLoading } = useQuery({
    queryKey: ["expense-reports", organizationId, isManager, userId],
    queryFn: async () => {
      // First get the reports
      let query = supabase
        .from("expense_reports")
        .select("*")
        .eq("organization_id", organizationId);

      // If not a manager, only show own reports
      if (!isManager) {
        query = query.eq("generated_by", userId);
      }

      query = query.order("created_at", { ascending: false });

      const { data: reportsData, error: reportsError } = await query;
      if (reportsError) throw reportsError;

      // Then get the user profiles for the generated_by users
      const userIds = reportsData.map(report => report.generated_by);
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      return reportsData.map(report => ({
        ...report,
        user: profilesData.find(profile => profile.id === report.generated_by)
      }));
    },
  });

  // Use useEffect to call onReportStatusChange when reports change
  useEffect(() => {
    if (reports && onReportStatusChange) {
      onReportStatusChange();
    }
  }, [reports, onReportStatusChange]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const handleReportClick = (reportId: string) => {
    console.log("🔍 DEBUG (ExpenseReportList): Navigating to report:", reportId);
    console.log("🔍 DEBUG (ExpenseReportList): Using organizationId:", organizationId);
    navigate(`/app/${organizationId}/expense-reports/${reportId}`);
  };

  if (isLoading) {
    return <div className="py-4 text-center">Loading reports...</div>;
  }

  if (!reports || reports.length === 0) {
    return <div className="py-4 text-center text-muted-foreground">No expense reports found.</div>;
  }

  return (
    <div className="space-y-4">
      {isMobile ? (
        <div className="space-y-4">
          {reports.map((report) => (
            <div 
              key={report.id}
              className="border rounded-lg p-4 cursor-pointer hover:bg-muted/50"
              onClick={() => handleReportClick(report.id)}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="text-sm">
                  {format(new Date(report.created_at), "MMM d, yyyy")}
                </div>
                <Badge className={`${getStatusColor(report.status)} capitalize`}>
                  {report.status}
                </Badge>
              </div>
              <div className="font-medium mb-1">${report.total_amount.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">
                {report.user?.first_name} {report.user?.last_name}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((report) => (
                <TableRow 
                  key={report.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleReportClick(report.id)}
                >
                  <TableCell>
                    {format(new Date(report.created_at), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    {report.user?.first_name} {report.user?.last_name}
                  </TableCell>
                  <TableCell>${report.total_amount.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className={`${getStatusColor(report.status)} capitalize`}>
                      {report.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ExpenseReportList;
